import { ClearOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Skeleton, Typography } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import api from '~/services/api'

import InspectionDrawer from './InspectionDrawer'
import { ModalHeader, PendingTable } from './styles'

const { Text } = Typography

export default function PendingInspectionModal({ open, onClose }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [serialFilter, setSerialFilter] = useState('')
  const [activeSerial, setActiveSerial] = useState('')

  const loadItems = useCallback(async (serial = '') => {
    setLoading(true)
    try {
      const params = serial ? `?serial_number=${encodeURIComponent(serial)}` : ''
      const res = await api.get(`/returns/to-be-inspected/latest-rma${params}`)
      setItems(res.data)
    } catch (err) {
      toast.error('Failed to load pending inspection items.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      setSerialFilter('')
      setActiveSerial('')
      loadItems()
    }
  }, [open, loadItems])

  const handleSearch = () => {
    const val = serialFilter.trim().toUpperCase()
    setActiveSerial(val)
    loadItems(val)
  }

  const handleClear = () => {
    setSerialFilter('')
    setActiveSerial('')
    loadItems('')
  }

  const handleRowDoubleClick = (record) => {
    if (loading || !record.id) return
    setSelectedItem(record)
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedItem(null)
  }

  const tableColumns = [
    {
      title: 'Return ID',
      dataIndex: 'id',
      key: 'id',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '60%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '75%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
    {
      title: 'Part Number',
      dataIndex: 'part_number',
      key: 'part_number',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
    {
      title: 'Store',
      dataIndex: 'store_name',
      key: 'store_name',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '65%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
    {
      title: 'Return Reason',
      dataIndex: 'return_reason',
      key: 'return_reason',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '85%' }} /> : value ?? <Text type="secondary">—</Text>),
    },
  ]

  const tableData = loading
    ? Array.from({ length: 5 }, (_, i) => ({ key: `skeleton-${i}` }))
    : items.map((item, i) => ({ ...item, key: `${item.id ?? 'null'}-${item.serial_number ?? i}` }))

  return (
    <>
      <Modal
        title={
          <ModalHeader>
            <span>RMA Technician Inspection</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Input
                placeholder="Filter by serial number"
                value={serialFilter}
                onChange={(e) => setSerialFilter(e.target.value.toUpperCase())}
                onPressEnter={handleSearch}
                suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                style={{ width: 220 }}
                size="small"
                allowClear={false}
              />
              {activeSerial && (
                <Button size="small" icon={<ClearOutlined />} onClick={handleClear}>
                  Clear
                </Button>
              )}
              <Button size="small" icon={<ReloadOutlined />} onClick={() => loadItems(activeSerial)} loading={loading}>
                Refresh
              </Button>
            </div>
          </ModalHeader>
        }
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={980}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
          Double-click a row to view full RMA details and add technician notes.
        </Text>
        <PendingTable
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onDoubleClick: () => handleRowDoubleClick(record),
            style: loading ? {} : { cursor: 'pointer' },
          })}
        />
      </Modal>

      <InspectionDrawer open={drawerOpen} onClose={handleDrawerClose} rmaId={selectedItem?.id} inventoryItemsId={selectedItem?.inventory_items_id ?? null} />
    </>
  )
}
